import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolMemberService } from '../../../../services/member.school.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import * as schoolMemberSelector from '../../../../state/selectors/member.school.selector';
import * as paginationSelector from '../../../../state/selectors/pagination.selector';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';
import { LoadingMemberComponent } from '../../../components/loaders/loading-member/loading-member.component';
import { DlgViewProfileComponent } from '../../../profile/dlg-view-profile/dlg-view-profile.component';
import { DlgAssignRolesComponent } from '../../dlg-assign-roles/dlg-assign-roles.component';
import { ChangeMemberStatusComponent } from '../change-member-status/change-member-status.component';
import { DlgAddStudentComponent } from '../dlg-add-student/dlg-add-student.component';
import { DlgPlanLimitComponent } from '../../../components/dialogs/dlg-plan-limit/dlg-plan-limit.component';
import { GroupFeatureAccessService } from '../../../../services/group-feature-access.service';

@Component({
  selector: 'app-list-parents',
  standalone: true,
  imports: [
    AsyncPipe,
    RouterLink,
    TitleCasePipe,
    MatDialogModule,
    BtnMoreComponent,
    BtnFabComponent,
    ChangeMemberStatusComponent,
    NgxSkeletonLoaderModule,
    MomentPipe,
    InitialsPipe,
    ListUtilsBarComponent,
    LoadingMemberComponent,
  ],
  templateUrl: './list-parents.component.html',
  styleUrl: './list-parents.component.css',
})
export class ListParentsComponent implements OnInit, AfterViewInit, OnDestroy {
  firstNewItemId: string | null = null;
  private cdr = inject(ChangeDetectorRef);
  toastr: ToastrService = inject(ToastrService);
  @ViewChildren('itemRef') itemRefs!: QueryList<ElementRef>;

  private store: Store<AppState> = inject(Store<AppState>);
  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  memberService: SchoolMemberService = inject(SchoolMemberService);
  private dialog: MatDialog = inject(MatDialog);
  private featureAccessService = inject(GroupFeatureAccessService);

  headers: string[] = ['Parent Name', 'Children', 'Joined Date', 'Actions'];

  parents$: Observable<SchoolMember[] | null | undefined> = new Observable();

  loading$: Subject<boolean> = new Subject<boolean>();
  isSearching$: Observable<boolean> = new Observable();
  showingSearchResults$: Observable<boolean> = new Observable();

  groupId: string | null = null;
  group: Group | null | undefined;

  seatsLimit: number = 25;
  isSubscribed: boolean = false;
  studentsCount: number = 0;

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  rolesType = SchoolRoles;
  utils = UtilsService;

  exit$: Subject<void> = new Subject<void>();

  constructor() {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.groupId = params.get('id');
      if (this.groupId) {
        const defaultFreeSeats = environment.STRIPE_SCHOOL_FREE_SEATS || 25;
        this.store
          .select(groupSelector.getGroupMetadataMap)
          .pipe(takeUntil(this.exit$))
          .subscribe((metadataMap) => {
            const metadata = metadataMap ? metadataMap[this.groupId!] : null;
            if (metadata) {
              this.seatsLimit = metadata.subscribedSeatsTier ?? defaultFreeSeats;
              this.isSubscribed = this.seatsLimit > defaultFreeSeats;
            } else {
              this.seatsLimit = defaultFreeSeats;
              this.isSubscribed = false;
            }
          });
      }
    });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.studentsCount = group?.students ?? 0;
      });

    this.loadParents();

    this.store.select(schoolMemberSelector.loading).subscribe((loading) => {
      this.loading$.next(loading);
    });
  }

  ngOnInit() {}

  ngAfterViewInit() {
    this.itemRefs.changes.subscribe(() => {
      if (this.firstNewItemId) {
        const el = this.itemRefs.find(
          (el) =>
            el.nativeElement.getAttribute('data-id') === this.firstNewItemId,
        );
        if (el) {
          el.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });

          requestAnimationFrame(() => {
            this.firstNewItemId = null;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.firstNewItemId = null;
              this.cdr.detectChanges();
            }, 2000);
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onAssignRole(item: SchoolMember) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Update Member Role(s)',
      description: `Select one or more roles to assign to '${item.name}'`,
      action: 'Update Roles',
      member: item,
    };
    const dialogRef = this.dialog.open(DlgAssignRolesComponent, dialogConfig);

    // return dialogRef.afterClosed().subscribe((result) => {
    //   if (result) {
    //     this.toastr.success(
    //       'Member role(s) updated successfully',
    //       'Update Success'
    //     );
    //   }
    //   return;
    // });
  }

  onAddStudent(parent: SchoolMember) {
    if (!this.featureAccessService.checkActionAllowed(true)) {
      return;
    }

    if (this.studentsCount >= this.seatsLimit) {
      DlgPlanLimitComponent.open(this.dialog, {
        groupId: this.groupId!,
        seatsLimit: this.seatsLimit,
        isSubscribed: this.isSubscribed,
        actionType: 'add_student',
      });
      return;
    }

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      groupId: this.groupId!,
      parent: parent,
    };
    const dialogRef = this.dialog.open(DlgAddStudentComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result) this.cdr.detectChanges();
      return;
    });
  }

  loadParents() {
    this.parents$ = this.store.select(
      schoolMemberSelector.getGroupMembersByType({
        memberType: [SchoolMemberType.parent],
        groupId: this.groupId,
        status: [MembershipStatus.active],
      }),
    );
  }

  addMore() {
    this.store
      .select(paginationSelector.getPaginationById(this.groupId!))
      .pipe(take(1))
      .subscribe((lastRecord) => {
        if (lastRecord?.lastParentRecord?.id) {
          this.firstNewItemId = lastRecord.lastParentRecord.id;
        }

        this.store.dispatch(
          memberActions.getMembers({
            source: 'ListParentsComponent :: addMore',
            groupId: this.groupId!,
            getMemberType: [SchoolMemberType.parent],
            status: [MembershipStatus.active],
            lastRecord: lastRecord,
          }),
        );
      });
  }

  onSearchTermChange(value: string) {
    this.store
      .select(
        memberSelector.getGroupMembersByType({
          memberType: [SchoolMemberType.parent],
          groupId: this.groupId,
          status: [MembershipStatus.active],
          filter: value,
        }),
      )
      .pipe(take(1))
      .subscribe((items) => {
        this.parents$ = of(items);
      });
  }

  async onSearch(term: string) {
    this.parents$ = of([]);
    this.isSearching$ = of(true);
    const result = await this.memberService.searchMembers(term, this.groupId!, {
      memberType: [SchoolMemberType.parent],
    });
    this.parents$ = of(result);
    this.isSearching$ = of(false);
    this.showingSearchResults$ = of(true);
  }

  onCancelSearch() {
    this.loadParents();
    this.isSearching$ = of(false);
    this.showingSearchResults$ = of(false);
  }

  onRefresh() {
    this.store.dispatch(
      memberActions.getMembers({
        source: 'ListParentsComponent :: refresh',
        groupId: this.groupId!,
        getMemberType: [SchoolMemberType.parent],
        status: [MembershipStatus.active],
      }),
    );
  }

  onViewProfile(item: SchoolMember) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      user: item,
      title: `${item.name}'s Details`,
      description: `Profile details available in group`,
    };
    this.dialog.open(DlgViewProfileComponent, dialogConfig);
  }
}
